import React, {createContext, useContext, useState} from 'react';
import {useSelector} from 'react-redux';
import {AtzmaiUsersApiService} from '../../../../services';
import {RootState} from '../../../../store/configureStore';
import {localeDirection} from '../../../utils';
import {pathnames as P} from '../../../enums';

export type LayoutDirection = 'ltr' | 'rtl';
export type GlobalContext = {
  DEBOUNCE_DELAY_MS: number,
  layoutDirection: LayoutDirection,
  goBackToOldApp: () => void,
  apiService: AtzmaiUsersApiService,
  pageTitle: React.ReactNode,
  setPageTitle: (title: React.ReactNode) => void,
  showGoBackToOldAppButton: boolean,
  supportMeteor: false,
  showPopupPanel: boolean,
  openPopupPanel: () => void,
  closePopupPanel: () => void,
  popupPanelContent: React.ReactNode,
  setPopupPanelContent: (content: React.ReactNode) => void,
}

export interface GlobalContextProviderProps {
  children: React.ReactNode;
}

export const defaultContext: GlobalContext = {
  DEBOUNCE_DELAY_MS: 500,
  layoutDirection: 'ltr',
  goBackToOldApp: () => {},
  apiService: new AtzmaiUsersApiService(''),
  pageTitle: null,
  setPageTitle: () => {},
  showGoBackToOldAppButton: true,
  supportMeteor: false,
  showPopupPanel: false,
  openPopupPanel: () => {},
  closePopupPanel: () => {},
  popupPanelContent: null,
  setPopupPanelContent: () => {},
};

export const GlobalContext = createContext<GlobalContext>(defaultContext);
GlobalContext.displayName = 'GlobalContext';

export const useGlobalContext = () => useContext(GlobalContext);

export const GlobalContextProvider = ({children}: GlobalContextProviderProps) => {
  const currentToken = useSelector((state: RootState) => state.auth.token) as string;
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);

  const apiService = new AtzmaiUsersApiService(currentToken);

  const supportMeteor = false;
  const showGoBackToOldAppButton = supportMeteor && window.location.pathname !== P.HOME;
  const goBackToOldApp = () => {
    window.location.href = process.env.REACT_APP_METEOR_APP_CLIENT_LINK;
  };

  const [pageTitle, setPageTitle] = useState<React.ReactNode>(null);

  const [showPopupPanel, setShowPopupPanel] = useState(false);
  const [popupPanelContent, setPopupPanelContent] = useState<React.ReactNode>(null);

  const context: GlobalContext = {
    ...defaultContext,
    layoutDirection: localeDirection(currentLocale),
    goBackToOldApp,
    apiService,
    pageTitle,
    setPageTitle,
    showGoBackToOldAppButton,
    supportMeteor,
    showPopupPanel: showPopupPanel,
    openPopupPanel: () => setShowPopupPanel(true),
    closePopupPanel: () => setShowPopupPanel(false),
    popupPanelContent,
    setPopupPanelContent,
  };

  return (
    <GlobalContext.Provider value={context}>
      {children}
    </GlobalContext.Provider>
  );
};
